import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type InviteTeamBannerDismissal,
  dismissInviteTeamBanner,
  getInviteTeamBannerDismissal,
  isAppBffConfigured,
} from './inviteTeamAlert.service';

// 90 days stands in for 3 calendar months, matching the mobile banner.
const RESHOW_AFTER_MS = 90 * 24 * 60 * 60 * 1000;

const queryKey = ['inviteTeamBannerDismissal'];

export const isDismissalActive = (dismissedAt: string | null, now = Date.now()): boolean => {
  if (!dismissedAt) {
    return false;
  }

  const dismissedTime = new Date(dismissedAt).getTime();
  return !Number.isNaN(dismissedTime) && now - dismissedTime < RESHOW_AFTER_MS;
};

export const useInviteTeamBannerDismissal = (enabled: boolean) => {
  const queryClient = useQueryClient();

  const { data, isSuccess } = useQuery({
    queryKey,
    queryFn: getInviteTeamBannerDismissal,
    enabled: enabled && isAppBffConfigured,
    retry: 1,
    staleTime: 1000 * 60 * 10,
  });

  const { mutate } = useMutation({
    mutationFn: dismissInviteTeamBanner,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previousDismissal = queryClient.getQueryData<InviteTeamBannerDismissal>(queryKey);
      queryClient.setQueryData<InviteTeamBannerDismissal>(queryKey, {
        dismissedAt: new Date().toISOString(),
      });
      return { previousDismissal };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDismissal !== undefined) {
        queryClient.setQueryData(queryKey, context.previousDismissal);
      }
    },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    // Hidden until we know otherwise, so a failed lookup never re-shows a banner
    // the user already dismissed.
    isDismissed: !isSuccess || isDismissalActive(data.dismissedAt),
    dismiss: mutate,
  };
};
